/**
 * Convert odds/probabilities to LMSR initial quantities
 *
 * LMSR price formula: p_i = exp(q_i / b) / Σexp(q_j / b)
 *
 * For quantities that sum to zero (required by contract):
 * q_i = b * ln(p_i) - b * ln(geometric_mean(p))
 *
 * Where geometric_mean(p) = (∏p_i)^(1/n)
 *
 * IMPORTANT: The contract computes b = (reserve * 1e18) / ln(bucketCount)
 * Quantities must be scaled relative to this b value.
 */

// =============================================================================
// Error Types for DevEx
// =============================================================================

/** Error codes for structured error handling in UI */
export type OddsValidationErrorCode =
	| "TOO_FEW_OUTCOMES"
	| "TOO_MANY_OUTCOMES"
	| "INVALID_SUM"
	| "PROBABILITY_TOO_LOW"
	| "PROBABILITY_TOO_HIGH"
	| "NEGATIVE_PROBABILITY"
	| "INVALID_RESERVE"
	| "OVERFLOW_RISK"

export interface OddsValidationResult {
	valid: boolean
	code?: OddsValidationErrorCode
	error?: string
}

// =============================================================================
// Constants
// =============================================================================

/** Maximum allowed magnitude for q/b ratio before exp() overflow
 * Solady's expWad can handle inputs up to ~130 (exp(130) ≈ 2^187) */
const MAX_EXP_INPUT = 100

/** Minimum probability percentage (0.1%) */
const MIN_PROBABILITY = 0.1

/** Maximum probability percentage (99.9%) */
const MAX_PROBABILITY = 99.9

/** Maximum number of outcomes */
const MAX_OUTCOMES = 10

/** Minimum number of outcomes */
const MIN_OUTCOMES = 2

// =============================================================================
// Main Functions
// =============================================================================

/**
 * Convert probability percentages to LMSR quantities.
 *
 * The quantities are scaled to be compatible with the contract's liquidity parameter.
 * The contract computes: b = (reserve * 1e18) / ln(bucketCount)
 *
 * @param odds - Array of probabilities as percentages (e.g., [60, 40] for 60%/40%)
 * @param reserveDollars - Initial reserve in whole dollars (e.g., 1000n for $1,000).
 *                         This is NOT micro-USDC - pass the human-readable dollar amount.
 *                         Internally scaled by 1e6 to compute quantities.
 * @returns Array of quantities as bigint (in micro-USDC scale) that sum to zero
 *
 * @throws Error if odds don't sum to 100, have < 2 outcomes, or would cause overflow
 *
 * @example
 * // 60/40 odds for a binary market with $1000 reserve
 * oddsToQuantities([60, 40], 1000n) // Pass 1000n, not 1000_000_000n
 *
 * @example
 * // Uniform odds (50/50) - returns zeros
 * oddsToQuantities([50, 50], 1000n)
 * // Returns [0n, 0n]
 */
export function oddsToQuantities(odds: number[], reserveDollars: bigint): bigint[] {
	// Validate reserve first
	if (reserveDollars <= 0n) {
		throw new Error("Reserve must be positive (> 0 USDC). Example: 1000n for $1,000")
	}

	// Validate odds structure
	if (odds.length < MIN_OUTCOMES) {
		throw new Error(
			`Need at least ${MIN_OUTCOMES} outcomes, got ${odds.length}. Example: [60, 40] for binary market`,
		)
	}

	if (odds.length > MAX_OUTCOMES) {
		throw new Error(
			`Maximum ${MAX_OUTCOMES} outcomes supported, got ${odds.length}. Consider splitting into multiple markets`,
		)
	}

	// Check for negative values (before sum check for clearer error)
	for (let i = 0; i < odds.length; i++) {
		const odd = odds[i]
		if (odd === undefined || Number.isNaN(odd) || !Number.isFinite(odd)) {
			throw new Error(`Outcome ${i + 1} has invalid value. Probabilities must be numbers.`)
		}
		if (odd < 0) {
			throw new Error(
				`Outcome ${i + 1} has negative probability (${odd}%). All probabilities must be positive.`,
			)
		}
	}

	// Validate odds sum to ~100
	const sum = odds.reduce((a, b) => a + b, 0)
	if (Math.abs(sum - 100) > 0.01) {
		const diff = sum - 100
		const suggestion = diff > 0 ? `reduce by ${diff.toFixed(1)}%` : `add ${(-diff).toFixed(1)}%`
		throw new Error(`Odds must sum to 100%, got ${sum.toFixed(1)}%. Please ${suggestion}.`)
	}

	// Check for extreme odds that could cause overflow
	for (let i = 0; i < odds.length; i++) {
		const odd = odds[i] ?? 0
		if (odd < MIN_PROBABILITY) {
			throw new Error(
				`Outcome ${i + 1} has ${odd.toFixed(2)}% probability. ` +
					`Minimum is ${MIN_PROBABILITY}% to avoid mathematical overflow.`,
			)
		}
		if (odd > MAX_PROBABILITY) {
			throw new Error(
				`Outcome ${i + 1} has ${odd.toFixed(2)}% probability. ` +
					`Maximum is ${MAX_PROBABILITY}% to avoid mathematical overflow.`,
			)
		}
	}

	// Check for uniform odds - return zeros (no price skew needed)
	const firstOdd = odds[0] ?? 0
	const isUniform = odds.every(o => Math.abs(o - firstOdd) < 0.01)
	if (isUniform) {
		return odds.map(() => 0n)
	}

	// Convert percentages to probabilities
	const probs = odds.map(o => o / 100)

	// Calculate geometric mean: (∏p_i)^(1/n)
	const logSum = probs.reduce((acc, p) => acc + Math.log(p), 0)
	const logGeometricMean = logSum / probs.length

	// Calculate the b parameter as the contract will compute it
	// Contract: b = (reserve * 1e18) / LnBuckets.ln(n)
	// where LnBuckets.ln returns ln(n) * 1e18
	// So: b = reserve / ln(n) (no extra 1e18 factor)
	// reserveDollars is in raw units (e.g., 1000 for $1000), we need to scale by 1e6
	const ln_n = Math.log(odds.length)
	const b = (Number(reserveDollars) * 1e6) / ln_n

	// Calculate quantities: q_i = b * (ln(p_i) - ln(geometric_mean))
	const quantities = probs.map((p, i) => {
		const logP = Math.log(p)
		const ratio = logP - logGeometricMean

		// Check that the ratio won't overflow exp() in the contract
		if (Math.abs(ratio) > MAX_EXP_INPUT) {
			throw new Error(
				`Outcome ${i + 1} (${odds[i]}%) is too extreme. ` +
					`The difference from other outcomes causes mathematical overflow. ` +
					`Try using odds between 1% and 99%.`,
			)
		}

		const q = b * ratio
		return BigInt(Math.round(q))
	})

	// Ensure quantities sum to exactly zero (fix any rounding errors)
	const quantitySum = quantities.reduce((a, b) => a + b, 0n)
	if (quantitySum !== 0n) {
		// Adjust the largest quantity to make sum exactly zero
		const maxIdx = quantities.reduce((maxI, q, i, arr) => (q > (arr[maxI] ?? 0n) ? i : maxI), 0)
		const currentMax = quantities[maxIdx] ?? 0n
		quantities[maxIdx] = currentMax - quantitySum
	}

	return quantities
}

/**
 * Convert LMSR quantities back to probability percentages
 * Useful for displaying current odds in UI
 *
 * @param quantities - Array of quantities (from contract or oddsToQuantities)
 * @param liquidityParam - The b parameter from the contract's liquidityParam() view
 * @returns Array of probabilities as percentages
 */
export function quantitiesToOdds(quantities: bigint[], liquidityParam: bigint): number[] {
	if (quantities.length < 2 || liquidityParam === 0n) {
		return quantities.map(() => 100 / quantities.length)
	}

	// Calculate exp(q_i / b) for each quantity
	// The contract computes exp((q * 1e18) / b) using expWad
	// But for round-tripping, we just compute exp(q / b) directly
	const expValues = quantities.map(q => {
		const ratio = Number(q) / Number(liquidityParam)
		return Math.exp(ratio)
	})

	// Sum of all exp values
	const sumExp = expValues.reduce((a, b) => a + b, 0)

	// Probability = exp(q_i / b) / sum
	const probs = expValues.map(exp => (exp / sumExp) * 100)

	return probs
}

/**
 * Validate that odds are reasonable for market creation.
 *
 * This performs the same validation as oddsToQuantities but returns
 * structured results instead of throwing, making it suitable for
 * real-time form validation.
 *
 * @param odds - Array of probabilities as percentages
 * @param reserveDollars - Optional reserve to validate (for overflow checks)
 * @returns Validation result with error code and message if invalid
 *
 * @example
 * const result = validateOdds([60, 40])
 * if (!result.valid) {
 *   showError(result.error)
 *   // Handle specific error types:
 *   if (result.code === 'PROBABILITY_TOO_LOW') {
 *     highlightLowProbabilityField()
 *   }
 * }
 */
export function validateOdds(odds: number[], reserveDollars?: bigint): OddsValidationResult {
	// Reserve validation (if provided)
	if (reserveDollars !== undefined && reserveDollars <= 0n) {
		return {
			valid: false,
			code: "INVALID_RESERVE",
			error: "Reserve must be positive (> 0 USDC)",
		}
	}

	// Outcome count validation
	if (odds.length < MIN_OUTCOMES) {
		return {
			valid: false,
			code: "TOO_FEW_OUTCOMES",
			error: `Need at least ${MIN_OUTCOMES} outcomes, got ${odds.length}`,
		}
	}

	if (odds.length > MAX_OUTCOMES) {
		return {
			valid: false,
			code: "TOO_MANY_OUTCOMES",
			error: `Maximum ${MAX_OUTCOMES} outcomes supported, got ${odds.length}`,
		}
	}

	// Check for invalid values (NaN, Infinity, negative)
	for (let i = 0; i < odds.length; i++) {
		const odd = odds[i]

		if (odd === undefined || Number.isNaN(odd) || !Number.isFinite(odd)) {
			return {
				valid: false,
				code: "NEGATIVE_PROBABILITY",
				error: `Outcome ${i + 1} has invalid value`,
			}
		}

		if (odd < 0) {
			return {
				valid: false,
				code: "NEGATIVE_PROBABILITY",
				error: `Outcome ${i + 1} has negative probability (${odd}%)`,
			}
		}

		if (odd < MIN_PROBABILITY) {
			return {
				valid: false,
				code: "PROBABILITY_TOO_LOW",
				error: `Outcome ${i + 1} must be at least ${MIN_PROBABILITY}%, got ${odd.toFixed(2)}%`,
			}
		}

		if (odd > MAX_PROBABILITY) {
			return {
				valid: false,
				code: "PROBABILITY_TOO_HIGH",
				error: `Outcome ${i + 1} must be at most ${MAX_PROBABILITY}%, got ${odd.toFixed(2)}%`,
			}
		}
	}

	// Sum validation
	const sum = odds.reduce((a, b) => a + b, 0)
	if (Math.abs(sum - 100) > 0.01) {
		return {
			valid: false,
			code: "INVALID_SUM",
			error: `Odds must sum to 100%, currently ${sum.toFixed(1)}%`,
		}
	}

	// Overflow risk check (only if not uniform and we have enough data)
	const firstOddVal = odds[0] ?? 0
	const isUniform = odds.every(o => Math.abs(o - firstOddVal) < 0.01)
	if (!isUniform) {
		const probs = odds.map(o => o / 100)
		const logSum = probs.reduce((acc, p) => acc + Math.log(p), 0)
		const logGeometricMean = logSum / probs.length

		for (let i = 0; i < probs.length; i++) {
			const prob = probs[i] ?? 0
			const logP = Math.log(prob)
			const ratio = logP - logGeometricMean

			if (Math.abs(ratio) > MAX_EXP_INPUT) {
				return {
					valid: false,
					code: "OVERFLOW_RISK",
					error: `Outcome ${i + 1} (${odds[i]}%) is too extreme relative to other outcomes`,
				}
			}
		}
	}

	return { valid: true }
}

/**
 * Validate multiple odds arrays at once.
 * Useful for batch market creation where you need to validate many configurations.
 *
 * @param oddsList - Array of odds arrays to validate
 * @param reserveDollars - Optional reserve for overflow validation
 * @returns Array of validation results, one per odds array
 */
export function validateMultipleOdds(
	oddsList: number[][],
	reserveDollars?: bigint,
): OddsValidationResult[] {
	return oddsList.map(odds => validateOdds(odds, reserveDollars))
}

/**
 * Check if odds represent uniform distribution (e.g., 50/50, 33.3/33.3/33.3)
 *
 * @param odds - Array of probabilities as percentages
 * @returns true if all odds are approximately equal
 */
export function isUniformOdds(odds: number[]): boolean {
	if (odds.length < 2) {
		return false
	}
	const first = odds[0] ?? 0
	return odds.every(o => Math.abs(o - first) < 0.01)
}

/**
 * Create uniform odds for a given number of outcomes.
 *
 * @param outcomeCount - Number of outcomes
 * @returns Array of equal probabilities summing to 100
 *
 * @example
 * createUniformOdds(2) // [50, 50]
 * createUniformOdds(5) // [20, 20, 20, 20, 20]
 */
export function createUniformOdds(outcomeCount: number): number[] {
	if (outcomeCount < MIN_OUTCOMES || outcomeCount > MAX_OUTCOMES) {
		throw new Error(`Outcome count must be between ${MIN_OUTCOMES} and ${MAX_OUTCOMES}`)
	}
	return Array(outcomeCount).fill(100 / outcomeCount)
}
